/**
 * Stable per-browser install id.
 *
 * `/py-api/v1/mobile/auth/login` requires a `deviceId` — it is how a session
 * is named in the "signed-in devices" list and what `revoke({deviceId})`
 * targets. A browser has no hardware id, so one is minted on first login and
 * kept next to the session.
 *
 * It is NOT a secret and NOT an authenticator: it labels a session, it does
 * not authorise one. Losing it costs nothing but a duplicate row in the device
 * list, which is why a browser that blocks storage silently gets a fresh id
 * per page load rather than failing to sign in.
 */

export const DEVICE_ID_STORAGE_KEY = "aim.device.v1";

function randomId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  if (c && typeof c.getRandomValues === "function") {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Read the stored id, minting and persisting one the first time. */
export function getOrCreateDeviceId(store?: Storage): string {
  const target = store ?? (typeof window === "undefined" ? undefined : window.localStorage);
  if (!target) return randomId();
  try {
    const existing = target.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing) return existing;
    const minted = randomId();
    target.setItem(DEVICE_ID_STORAGE_KEY, minted);
    return minted;
  } catch {
    return randomId();
  }
}

/**
 * A human label for the devices list. Coarse on purpose — enough for the
 * owner to recognise "the laptop" without recording a fingerprint.
 */
export function describeBrowser(userAgent?: string): string {
  const ua = userAgent ?? (typeof navigator === "undefined" ? "" : navigator.userAgent);
  if (!ua) return "Web browser";

  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\//.test(ua)
      ? "Opera"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Safari\//.test(ua)
            ? "Safari"
            : "Browser";

  const os = /Windows/.test(ua)
    ? "Windows"
    : /Android/.test(ua)
      ? "Android"
      : /iPhone|iPad|iPod/.test(ua)
        ? "iOS"
        : /Mac OS X/.test(ua)
          ? "macOS"
          : /Linux/.test(ua)
            ? "Linux"
            : null;

  return os ? `${browser} on ${os}` : browser;
}
